import { FC } from 'react';
import { useSelector } from 'react-redux';
import FirstLevelTable from './components/FirstLevelTable';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import {
  SupplementaryRulesBackendFirstTableConfig,
  SupplementaryRulesBackendSecondTableConfig,
  SupplementaryRulesFirstTableConfig,
  SupplementaryRulesPageModel,
  SupplementaryRulesPayload,
  SupplementaryRulesPosition,
  SupplementaryRulesSecondTableConfig,
} from '../../../../api/rs-hg/risk/supplementaryRules.types';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
  toTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import { categoryProviderByNumber } from '../../../../utilities/categoryProvider';
import { activeSectionSelector } from '../../../../redux/pages/selectors';
import { useSupplementaryRulesPageQuery } from '../../../../api/rs-hg/risk/hooks/queries';

function buildFirstTableConfigFromSchema(
  section: string,
  schema: SupplementaryRulesBackendFirstTableConfig,
): SupplementaryRulesFirstTableConfig {
  return {
    noteSubject: `${section}_supplementary-rules`,
    noteTopicPrefix: 'rule_',
    columns: schema.columns.map((col) => {
      if (col.column_key === '#') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'row_number',
        };
      }

      if (col.column_key === 'status') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'status',
        };
      }

      if (col.column_key === 'notes') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'notes',
        };
      }

      return {
        column_header: col.column_name,
        column_key: col.column_key,
        type: 'text',
      };
    }),
  };
}

function buildSecondTableConfigFromSchema(
  section: string,
  schema: SupplementaryRulesBackendSecondTableConfig,
): SupplementaryRulesSecondTableConfig {
  return {
    parentMatchField: schema.parentMatchField,
    childMatchField: schema.childMatchField,
    noteSubject: `${section}_supplementary-rules`,
    columns: schema.columns.map((col) => {
      if (col.column_key === '#' || col.column_key === 'number') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'row_number',
        };
      }

      if (col.column_key === 'gm_id') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            categoryProviderByNumber(position.gm_id)[1],
        };
      }

      if (col.column_key === 'raw_position') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            addCommasToNumbersAndRound(position.raw_position),
        };
      }

      if (col.column_key === 'market_price') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            toTwoDecimalPlaces(position.market_price),
        };
      }

      if (col.column_key === 'risk_metric_value') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            addCommasToNumbersAndRound(position.risk_metric_value),
        };
      }

      if (col.column_key === 'exposure') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            percentageToTwoDecimalPlaces(position.exposure),
        };
      }

      return {
        column_header: col.column_name,
        column_key: col.column_key,
        type: 'text',
        sourceKey: col.column_key,
      };
    }),
  };
}

type SupplementaryRulesLoadedPageModel = SupplementaryRulesPageModel & {
  returned_position_date: string;
  data: SupplementaryRulesPayload;
};

function hasSupplementaryRulesPageModel(
  pageData: SupplementaryRulesPageModel | null | undefined,
): pageData is SupplementaryRulesLoadedPageModel {
  return Boolean(
    pageData?.returned_position_date &&
      pageData.data?.limit_status?.length &&
      pageData.data.schema?.firstTable &&
      pageData.data.schema?.secondTable,
  );
}

const SupplementaryRulesPage: FC<FundInfoComponentProps> = ({
  fundId,
  fundName,
  positionDate,
}) => {
  const supplementaryRulesQuery = useSupplementaryRulesPageQuery(
    fundId,
    positionDate,
  );
  const isPositionDateLoading = !!fundId && !positionDate;
  const section = useSelector(activeSectionSelector) || '';
  const pageData = hasSupplementaryRulesPageModel(
    supplementaryRulesQuery.data,
  )
    ? supplementaryRulesQuery.data
    : null;

  return (
    <QueryBoundary<SupplementaryRulesLoadedPageModel>
      isLoading={isPositionDateLoading || supplementaryRulesQuery.isLoading}
      isFetching={supplementaryRulesQuery.isFetching}
      isError={supplementaryRulesQuery.isError}
      error={supplementaryRulesQuery.error}
      data={pageData}
      loadingMessages={
        isPositionDateLoading
          ? ['Loading position date...']
          : ['Loading Supplementary Rules Data']
      }
      noDataMessage="No supplementary rules data available."
      errorMessage="Unable to load supplementary rules data."
    >
      {(pageData) => {
        const firstTableConfig = buildFirstTableConfigFromSchema(
          section,
          pageData.data.schema.firstTable,
        );
        const secondTableConfig = buildSecondTableConfigFromSchema(
          section,
          pageData.data.schema.secondTable,
        );

        return (
          <FirstLevelTable
            fundDetails={{ id: fundId, name: fundName }}
            supplementaryRulesData={pageData.data}
            selectedPositionDate={pageData.returned_position_date}
            firstTable={firstTableConfig}
            secondTable={secondTableConfig}
          />
        );
      }}
    </QueryBoundary>
  );
};

export default SupplementaryRulesPage;
